import React, { useState } from "react";
import { Leaf, ChevronDown, Sparkles } from "lucide-react";
import MICRO_NOTES from "@/lib/microNotes";

// Same RDA table used by the backend engine — kept in sync intentionally.
// See /app/backend/engine.py MICRO_RDA. Values reflect NHS adult reference
// nutrient intakes. Iron uses the female value when the athlete's profile
// says female, matching what the personalised rollup does. "What it does"
// notes live in @/lib/microNotes so `WeeklyMicros` shares the same copy.
const REF = [
  { key: "b12",       label: "Vitamin B12",  unit: "mcg", rda: 2.5,  v6: true },
  { key: "iron",      label: "Iron",         unit: "mg",  rda: 14,   v6: true, rdaFemale: 18 },
  { key: "zinc",      label: "Zinc",         unit: "mg",  rda: 10,   v6: true },
  { key: "calcium",   label: "Calcium",      unit: "mg",  rda: 800,  v6: true },
  { key: "vitd",      label: "Vitamin D",    unit: "mcg", rda: 15,   v6: true },
  { key: "omega3",    label: "Omega-3",      unit: "mg",  rda: 250,  v6: true },
  { key: "vita",      label: "Vitamin A",    unit: "mcg", rda: 800 },
  { key: "vitc",      label: "Vitamin C",    unit: "mg",  rda: 80 },
  { key: "vite",      label: "Vitamin E",    unit: "mg",  rda: 12 },
  { key: "vitk",      label: "Vitamin K",    unit: "mcg", rda: 75 },
  { key: "b1",        label: "Vitamin B1",   unit: "mg",  rda: 1.1 },
  { key: "b2",        label: "Vitamin B2",   unit: "mg",  rda: 1.4 },
  { key: "b3",        label: "Vitamin B3",   unit: "mg",  rda: 16 },
  { key: "b6",        label: "Vitamin B6",   unit: "mg",  rda: 1.4 },
  { key: "folate",    label: "Folate",       unit: "mcg", rda: 200 },
  { key: "magnesium", label: "Magnesium",    unit: "mg",  rda: 375 },
  { key: "potassium", label: "Potassium",    unit: "mg",  rda: 2000 },
  { key: "selenium",  label: "Selenium",     unit: "mcg", rda: 55 },
  { key: "iodine",    label: "Iodine",       unit: "mcg", rda: 150 },
  { key: "fibre",     label: "Fibre",        unit: "g",   rda: 30 },
];

function fmt(n) {
  // Keep small numbers (< 10) at 1 dp, otherwise integer for readability.
  return n < 10 ? String(n) : String(Math.round(n));
}

function RefRow({ row, isFemale }) {
  const rdaDay = (isFemale && row.rdaFemale) ? row.rdaFemale : row.rda;
  const rdaWeek = rdaDay * 7;
  return (
    <div className="py-2" data-testid={`ref-${row.key}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-zinc-100 truncate">{row.label}</span>
        <span className="text-[11px] font-mono shrink-0">
          <span className="text-white">{fmt(rdaDay)}{row.unit}</span>
          <span className="text-zinc-400">/day · </span>
          <span className="text-[#A3E635]">{fmt(rdaWeek)}{row.unit}</span>
          <span className="text-zinc-400">/wk</span>
        </span>
      </div>
      {row.note && <p className="text-[10px] text-zinc-300 mt-0.5">{row.note}</p>}
      {!row.note && MICRO_NOTES[row.key] && <p className="text-[10px] text-zinc-300 mt-0.5 leading-snug">{MICRO_NOTES[row.key]}</p>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Reference table shown to Pro users whose active plan has meal planning
// switched off. Same six endurance-critical micros surfaced first, with a
// collapsible full panel for the other 14.
// -----------------------------------------------------------------------------
export default function VitaminsReference({ profile }) {
  const [showFull, setShowFull] = useState(false);
  const isFemale = profile?.sex === "female";
  const veggie6 = REF.filter((r) => r.v6);
  const rest = REF.filter((r) => !r.v6);

  return (
    <div className="border border-white/10 bg-[#141414]" data-testid="vitamins-reference">
      <header className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
        <Leaf className="h-4 w-4 text-[#A3E635]" />
        <h3 className="font-display uppercase tracking-[0.15em] text-sm text-white font-bold">Recommended intakes</h3>
        <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-auto">NHS RDA{isFemale ? " · female" : ""}</span>
      </header>

      <div className="p-4">
        <p className="text-[11px] text-zinc-200 mb-3 leading-relaxed">
          <span className="text-white">Meal planning is off, so we can't tally what you've had.</span> Here's what to aim for — the six most likely to run low under endurance training show first, the rest are one tap away.
        </p>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="ref-veggie6">
          {veggie6.map((row) => <RefRow key={row.key} row={row} isFemale={isFemale} />)}
        </div>

        <button type="button" data-testid="ref-full-toggle" onClick={() => setShowFull((v) => !v)}
          className="mt-4 text-[10px] uppercase tracking-wider text-[#E2E30A] hover:text-white flex items-center gap-1 transition-colors">
          <Sparkles className="h-3 w-3" /> {showFull ? "Hide full panel" : "Show full micronutrient panel"}
          <ChevronDown className={`h-3 w-3 transition-transform ${showFull ? "rotate-180" : ""}`} />
        </button>

        {showFull && (
          <div className="mt-3 pt-3 border-t border-white/5 grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="ref-full-panel">
            {rest.map((row) => <RefRow key={row.key} row={row} isFemale={isFemale} />)}
          </div>
        )}

        <p className="mt-4 text-[10px] text-zinc-400 leading-relaxed">
          Reference only — the RDA is a population floor, not an athlete target. Individual needs vary; consult a Registered Sports Dietitian before starting any supplement.
        </p>
      </div>
    </div>
  );
}
